<template>
  <button
    :type="type"
    class="button"
    :class="buttonClasses"
    :disabled="isDisabled"
    v-on="$listeners"
  >
    <div :class="{ hidden: isLoading }" data-test-id="content">
      <slot></slot>
    </div>
    <slot name="spinner">
      <div v-if="isLoading" class="spinner-container flex--center--center">
        <app-spinner :transparent-mode="inverted" class="spinner" />
      </div>
    </slot>
  </button>
</template>

<script>
import AppSpinner from '@/components/common/AppSpinner.vue';

export default {
  name: 'AppButton',
  components: { AppSpinner },
  props: {
    type: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inverted: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    faded: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    fillWidth: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses() {
      return {
        'button--inverted': this.inverted,
        'button--small': this.small,
        'button--faded': this.faded,
        'button--dark-theme': this.dark,
        'button--fill-width': this.fillWidth
      };
    },
    isDisabled() {
      return this.disabled || this.isLoading;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/colors.scss';
@import '../../assets/scss/spinner.scss';

.button {
  position: relative;
  padding: 1rem 2.5rem;

  background-color: $tuatara-color;
  color: $white-color;

  font-family: inherit;
  font-size: 1rem;
  line-height: 1.3rem;
  font-weight: 600;
  letter-spacing: 0.2em;

  cursor: pointer;
  outline: none;
  border: 1px solid transparent;
  border-radius: 2px;

  &:hover {
    background-color: $cod-gray-color;
  }

  &:disabled {
    cursor: default;
    background-color: var(--u-color-grey-200);
  }

  &.button--small {
    padding: 6px 20px 8px;
    border-color: $westar-color;
    color: $tuatara-color;
    font-size: 1.2rem;
    line-height: 1.4rem;
    font-weight: 400;
    letter-spacing: normal;
    background-color: transparent;

    &:hover {
      background-color: transparent;
      border-color: $concord-color;
    }

    &:disabled {
      background-color: transparent;
      border-color: $westar-color;
    }
  }

  &.button--small {
    padding: 6px 20px 8px;
    border-color: $westar-color;
    color: $tuatara-color;
    font-size: 1.2rem;
    line-height: 1.4rem;
    font-weight: 400;
    letter-spacing: normal;
    background-color: transparent;

    &:hover {
      background-color: transparent;
      border-color: $concord-color;
    }

    &:disabled {
      background-color: transparent;
      border-color: $westar-color;
    }
  }

  &.button--small:lang(ar) {
    font-size: 1.4rem;
    line-height: 1.6rem;
  }

  &--fill-width {
    width: 100%;
  }

  &--inverted {
    background-color: transparent;
    border-color: $concord-color;
    color: $tuatara-color;

    &:hover {
      background-color: $tuatara-color;
      border-color: $tuatara-color;
      color: $white-color;
    }

    &:disabled {
      background-color: transparent;
      border-color: $concord-color;
      color: $tuatara-color;
    }

    .spinner .lds-ring div {
      border-color: black transparent transparent transparent;
    }
  }

  &--faded {
    background-color: $dawn-pink-color;
    border-color: $dawn-pink-color;
    color: $tide-color;

    &:hover {
      background-color: $dawn-pink-color;
    }

    &:disabled {
      background-color: $dawn-pink-color;
    }
  }

  &--dark-theme {
    &.button--small {
      color: $white-color;
      border-color: $concord-color;

      &:hover {
        border-color: $westar-color;
      }
    }
  }
}

.button:lang(ar) {
  font-size: 1.2rem;
  line-height: 1.5rem;
  letter-spacing: normal;
}

@media (min-width: 600px) {
  .button {
    font-size: 1.1rem;
    line-height: 1.4rem;

    &.button--small {
      font-size: 1.3rem;
      line-height: 1.6rem;
    }

    &.button--small:lang(ar) {
      font-size: 1.5rem;
      line-height: 1.8rem;
    }
  }

  .button:lang(ar) {
    font-size: 1.3rem;
    line-height: 1.6rem;
  }
}

@media (min-width: 960px) {
  .button {
    padding: 1.3rem 3.5rem;
    font-size: 1.2rem;
    line-height: 1.5rem;

    &.button--small {
      font-size: 1.4rem;
      line-height: 1.7rem;
    }

    &.button--small:lang(ar) {
      font-size: 1.6rem;
      line-height: 1.9rem;
    }
  }

  .button:lang(ar) {
    font-size: 1.4rem;
    line-height: 1.7rem;
  }
}

@media (min-width: 1264px) {
  .button {
    font-size: 1.3rem;
    line-height: 1.6rem;

    &.button--small {
      font-size: 1.6rem;
      line-height: 1.9rem;
    }

    &.button--small:lang(ar) {
      font-size: 1.8rem;
      line-height: 2.1rem;
    }
  }

  .button:lang(ar) {
    font-size: 1.5rem;
    line-height: 1.8rem;
  }
}
</style>
